import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  DocumentIcon,
  FolderIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert, Badge, Button, Input, Spinner } from '../../components/ui';
import { AXIOS_INSTANCE } from '../../services/api-client';

type ShareNodeType = 'folder' | 'microsite' | 'external_link';

interface ShareChild {
  id: number;
  node_type: ShareNodeType;
  title: string;
  slug: string;
  path: string;
  description: string | null;
  external_url: string | null;
}

interface ShareResponse {
  node_type: ShareNodeType;
  title: string;
  slug: string;
  path: string;
  description: string | null;
  html_content: string | null;
  external_url: string | null;
  children: ShareChild[];
}

interface ShareMagicLinkRedeemResponse {
  redirect_path: string;
}

const PREVIEW_IFRAME_SANDBOX = 'allow-scripts allow-downloads allow-popups allow-popups-to-escape-sandbox';

function normalizedSharePath(path: string | undefined) {
  return (path || '').split('/').filter(Boolean).join('/');
}

function childIcon(type: ShareNodeType) {
  if (type === 'folder') return <FolderIcon className="h-5 w-5 text-gray-400" />;
  if (type === 'external_link') return <LinkIcon className="h-5 w-5 text-gray-400" />;
  return <DocumentIcon className="h-5 w-5 text-gray-400" />;
}

export default function ShareFolderPage() {
  const params = useParams<{ token?: string; '*': string }>();
  const token = params.token;
  const sharePath = normalizedSharePath(params['*']);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [accessRequestResult, setAccessRequestResult] = useState<{ status: 'queued' | 'denied'; message: string } | null>(null);
  const isMagicRoute = Boolean(token);

  const redeemQuery = useQuery<ShareMagicLinkRedeemResponse>({
    queryKey: ['share-magic-redeem', token],
    queryFn: () =>
      AXIOS_INSTANCE.post(`/v1/microsite-generator/public/share/magic/${token}`).then((r) => r.data),
    enabled: isMagicRoute && Boolean(token),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (redeemQuery.data?.redirect_path) {
      navigate(redeemQuery.data.redirect_path, { replace: true });
    }
  }, [navigate, redeemQuery.data?.redirect_path]);

  const shareQuery = useQuery<ShareResponse>({
    queryKey: ['public-share-path', sharePath],
    queryFn: () =>
      AXIOS_INSTANCE.get(`/v1/microsite-generator/public/share/${sharePath}`).then((r) => r.data),
    enabled: !isMagicRoute && Boolean(sharePath),
    retry: false,
  });

  const accessRequestMutation = useMutation({
    mutationFn: (recipientEmail: string) =>
      AXIOS_INSTANCE.post(`/v1/microsite-generator/public/share/${sharePath}/access-requests`, {
        email: recipientEmail,
      }).then((r) => r.data),
    onSuccess: (data) => setAccessRequestResult(data),
  });

  const statusCode = (shareQuery.error as { response?: { status?: number } } | null)?.response?.status;
  const accessRequired = statusCode === 401;

  useEffect(() => {
    if (!shareQuery.data?.title) return;
    const previousTitle = document.title;
    document.title = shareQuery.data.title;
    return () => {
      document.title = previousTitle;
    };
  }, [shareQuery.data?.title]);

  const handleAccessRequest = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    accessRequestMutation.mutate(email);
  };

  if (isMagicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg p-6">
        {redeemQuery.isError ? (
          <Alert variant="error">This share link is invalid or has already been used.</Alert>
        ) : (
          <Spinner size="lg" />
        )}
      </div>
    );
  }

  if (!sharePath) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg p-6">
        <Alert variant="error">This share link is unavailable.</Alert>
      </div>
    );
  }

  if (accessRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg p-6">
        <div className="w-full max-w-md">
          {accessRequestResult ? (
            <Alert variant={accessRequestResult.status === 'denied' ? 'error' : 'success'}>
              {accessRequestResult.status === 'denied'
                ? accessRequestResult.message
                : 'Check your email for a secure access link.'}
            </Alert>
          ) : (
            <form
              onSubmit={handleAccessRequest}
              className="rounded-lg border border-gray-200 bg-white p-5 space-y-4 dark:border-dark-border dark:bg-dark-surface"
            >
              <div>
                <h1 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
                  Request access
                </h1>
                <p className="mt-1 text-small text-gray-500 dark:text-gray-400">
                  Enter your email and we'll send a secure access link.
                </p>
              </div>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
              {accessRequestMutation.isError && (
                <Alert variant="error">Unable to send access email. Please try again.</Alert>
              )}
              <Button type="submit" disabled={accessRequestMutation.isPending} className="w-full justify-center">
                {accessRequestMutation.isPending ? 'Sending...' : 'Send access link'}
              </Button>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (shareQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <Spinner size="lg" />
      </div>
    );
  }

  const share = shareQuery.data;
  if (shareQuery.error || !share) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg p-6">
        <Alert variant="error">This share link is unavailable.</Alert>
      </div>
    );
  }

  if (share.node_type === 'microsite') {
    return (
      <div className="h-screen bg-gray-50 dark:bg-dark-bg">
        {share.html_content ? (
          <iframe
            srcDoc={share.html_content}
            sandbox={PREVIEW_IFRAME_SANDBOX}
            referrerPolicy="no-referrer"
            title={share.title}
            className="h-full w-full border-0"
          />
        ) : (
          <div className="flex h-full items-center justify-center p-6">
            <Alert variant="error">This microsite is unavailable.</Alert>
          </div>
        )}
      </div>
    );
  }

  if (share.node_type === 'external_link' && share.external_url) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg p-6">
        <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
          {share.path.includes('/') && (
            <Link
              to={`/share/${share.path.split('/').slice(0, -1).join('/')}`}
              className="inline-flex items-center gap-1.5 text-small text-gray-500 hover:text-charcoal dark:text-gray-400 dark:hover:text-gray-100"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
            </Link>
          )}
          <h1 className="mt-4 font-subtitle text-h2 text-charcoal dark:text-gray-100">
            {share.title}
          </h1>
          {share.description && (
            <p className="mt-2 text-small text-gray-500 dark:text-gray-400">{share.description}</p>
          )}
          <a
            href={share.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-eliza-red px-4 py-2 text-small font-medium text-white hover:bg-eliza-red-light"
          >
            Open link
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      <main className="mx-auto max-w-5xl px-5 py-8">
        <div className="mb-6">
          <Badge variant="success">Shared folder</Badge>
          <h1 className="mt-3 font-title text-h1 text-charcoal dark:text-gray-100">{share.title}</h1>
          {share.description && (
            <p className="mt-2 max-w-2xl text-body text-gray-500 dark:text-gray-400">{share.description}</p>
          )}
        </div>

        {share.children.length === 0 ? (
          <Alert variant="info">No items are currently available.</Alert>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {share.children.map((child) => (
              child.node_type === 'external_link' && child.external_url ? (
                <a
                  key={`${child.node_type}-${child.id}`}
                  href={child.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 dark:border-dark-border dark:bg-dark-surface dark:hover:border-gray-600"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      {childIcon(child.node_type)}
                      <h2 className="truncate font-subtitle text-h3 text-charcoal dark:text-gray-100">
                        {child.title}
                      </h2>
                    </div>
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  {child.description && (
                    <p className="mt-2 text-small text-gray-500 dark:text-gray-400">{child.description}</p>
                  )}
                </a>
              ) : (
                <Link
                  key={`${child.node_type}-${child.id}`}
                  to={`/share/${child.path}`}
                  className="rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 dark:border-dark-border dark:bg-dark-surface dark:hover:border-gray-600"
                >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    {childIcon(child.node_type)}
                    <h2 className="truncate font-subtitle text-h3 text-charcoal dark:text-gray-100">
                      {child.title}
                    </h2>
                  </div>
                  {child.node_type === 'external_link' && <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />}
                </div>
                {child.description && (
                  <p className="mt-2 text-small text-gray-500 dark:text-gray-400">{child.description}</p>
                )}
                </Link>
              )
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
